import { Injectable } from '@angular/core';
import WaToast, { ToastCreateOptions } from '@web.awesome.me/webawesome-pro/dist/components/toast/toast.js';

const defaultToastOptions: ToastCreateOptions = {
    icon: 'circle-info',
    // Web Awesome accepts both short ('s') and long ('small') size aliases, but the
    // vendored v3.3.1 ToastCreateOptions type only declares the long forms — so this
    // one programmatic call uses 'small' (identical to 's' at runtime). Templates
    // keep the short 's' since custom-element attributes aren't checked against it.
    size: 'small',
    variant: 'neutral',
    duration: 5000,
    allowHtml: false,
};

/** Marks a toast stack this service created inside an open modal. */
const DIALOG_OUTLET_ATTR = 'data-toast-outlet';

@Injectable({
    providedIn: 'root',
})
export class ToastService {
    private _toastContainer: WaToast | null = null;

    private get toastContainer(): WaToast | null {
        if (this._toastContainer != null) {
            return this._toastContainer;
        }

        this._toastContainer = document.getElementById('app-toast-container') as unknown as WaToast;

        if (!this._toastContainer) {
            throw new Error('Toast container not found');
        }

        return this._toastContainer;
    }

    private changeFutureToastPlacement(placement: WaToast['placement'] = 'top-end') {
        if (this._toastContainer) {
            this._toastContainer.placement = placement;
        }
    }

    /**
     * The innermost open modal, or null when nothing modal is up.
     * Innermost because dialogs nest.
     */
    private openModalHost(): HTMLElement | null {
        const hosts = Array.from(document.querySelectorAll<HTMLElement>('wa-dialog, wa-drawer'));
        const open = hosts.filter(host => {
            const native = (host as HTMLElement & { shadowRoot: ShadowRoot | null }).shadowRoot?.querySelector('dialog');
            return native instanceof HTMLDialogElement && native.matches(':modal');
        });
        if (open.length === 0) {
            return null;
        }
        // The one with no other open modal inside it.
        return open.find(host => !open.some(other => other !== host && host.contains(other))) ?? open[open.length - 1];
    }

    /**
     * Get-or-create a toast stack inside `host`. Being a
     * DESCENDANT of the open dialog is the only thing that lifts it.
     */
    private dialogOutlet(host: HTMLElement): WaToast {
        const existing = host.querySelector<HTMLElement>(`:scope > wa-toast[${DIALOG_OUTLET_ATTR}]`);
        if (existing) {
            return existing as unknown as WaToast;
        }

        // Kept as HTMLElement for DOM work: the vendored WaToast type models the
        // component's own API, not the Element interface.
        const outlet = document.createElement('wa-toast') as unknown as HTMLElement;
        outlet.setAttribute(DIALOG_OUTLET_ATTR, '');
        (outlet as unknown as WaToast).placement = this.toastContainer?.placement ?? 'top-end';
        host.appendChild(outlet);

        // A toast raised just before the dialog closes would otherwise be hidden with
        // it — e.g. "Workspace deleted." fires and the modal closes in the same tick.
        // Hand any survivors back to the global stack so they finish their duration.
        host.addEventListener('wa-after-hide', event => {
            if (event.target !== host) {
                return; // a nested dialog closing, not this one
            }
            this.rescueToasts(outlet);
        });

        return outlet as unknown as WaToast;
    }

    /** Move still-live toasts out of a closing dialog and into the global stack. */
    private rescueToasts(outlet: HTMLElement) {
        const global = this.toastContainer as unknown as HTMLElement | null;
        if (!global) {
            return;
        }
        for (const item of Array.from(outlet.children)) {
            global.appendChild(item);
        }
    }

    /**
     * Route the toast into the innermost open modal when there is one, so it is
     * clickable; otherwise use the global stack. Resolved per call.
     */
    private resolveTarget(): WaToast | null {
        const modal = this.openModalHost();
        return modal ? this.dialogOutlet(modal) : this.toastContainer;
    }

    private isAlreadyOpen(msg: string, target: WaToast): boolean {
        const hosts = new Set<HTMLElement>();
        const asHost = target as unknown as HTMLElement;
        hosts.add(asHost);
        const global = this.toastContainer as unknown as HTMLElement | null;
        if (global) {
            hosts.add(global);
        }
        for (const host of hosts) {
            for (const item of Array.from(host.children)) {
                if ((item.textContent ?? '').includes(msg)) {
                    return true;
                }
            }
        }
        return false;
    }

    doCreateToast(msg: string, options = defaultToastOptions) {
        const target = this.resolveTarget()!;
        if (this.isAlreadyOpen(msg, target)) {
            return;
        }
        return void target.create(msg, options);
    }

    success(msg: string, options?: Partial<ToastCreateOptions>) {
        return this.doCreateToast(msg, { ...defaultToastOptions, variant: 'success', icon: 'circle-check', ...options });
    }

    error(msg: string, options?: Partial<ToastCreateOptions>) {
        return this.doCreateToast(msg, { ...defaultToastOptions, variant: 'danger', icon: 'circle-xmark', duration: 8000, ...options });
    }

    warning(msg: string, options?: Partial<ToastCreateOptions>) {
        return this.doCreateToast(msg, { ...defaultToastOptions, variant: 'warning', icon: 'circle-exclamation', ...options });
    }

    info(msg: string, options?: Partial<ToastCreateOptions>) {
        return this.doCreateToast(msg, { ...defaultToastOptions, variant: 'brand', icon: 'circle-info', ...options });
    }

    /** An info toast with an action button. The toast's own close button only closes it. */
    async infoWithAction(
        msg: string,
        actionLabel: string,
        onAction: () => void,
        options?: Partial<ToastCreateOptions> & { actionTestId?: string }
    ): Promise<void> {
        const target = this.resolveTarget();
        if (!target || this.isAlreadyOpen(msg, target)) {
            return;
        }
        const { actionTestId, ...toastOptions } = options ?? {};
        const item = await target.create(msg, { ...defaultToastOptions, variant: 'brand', icon: 'bell', duration: 10000, ...toastOptions });
        const action = document.createElement('button');
        action.type = 'button';
        action.className = 'toast-action';
        action.dataset['testid'] = actionTestId ?? 'toast-action-button';
        action.textContent = actionLabel;
        action.style.cssText = [
            'display:block',
            'margin-top:0.35rem',
            'padding:0',
            'border:0',
            'background:none',
            'color:inherit',
            'font:inherit',
            'font-weight:600',
            'text-decoration:underline',
            'cursor:pointer',
        ].join(';');
        action.addEventListener('click', () => {
            onAction();
            void item.hide();
        });
        item.append(action);
    }
}
